import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { getActiveWeeklyPlan } from "../../../../../../common/network/nutritionPlanApi";
import {
  NutritionDailyType,
  NutritionWeeklyType,
  PlanMealRootItemType,
} from "../model/nutritionPlanTypes";
import { addDaysISO, thisWeekMonday, todayISO } from "../helpers/planDates";
import {
  createInitialState,
  DetailsSelection,
  IntakeSelection,
  LoadStatus,
  nutritionPlanReducer,
  selectDay,
} from "./nutritionPlanReducer";

/**
 * Fetchers are injectable so tests (and a fixture mode) can run the provider
 * without the network. Production uses `nutritionPlanApi`.
 */
export type NutritionPlanFetchers = {
  getWeekly: (
    monday: string,
    role: number
  ) => Promise<NutritionWeeklyType | null>;
};

const defaultFetchers: NutritionPlanFetchers = {
  getWeekly: getActiveWeeklyPlan,
};

export type NutritionPlanContextValue = {
  weekStartDate: string;
  activeWeek: NutritionWeeklyType | undefined;
  activeWeekStatus: LoadStatus;
  details: DetailsSelection | null;
  intake: IntakeSelection | null;
  /** Whether the viewer may log/skip/swap. Always false in Phase 1. */
  canEdit: boolean;
  goToWeek: (monday: string) => void;
  goToPreviousWeek: () => void;
  goToNextWeek: () => void;
  goToThisWeek: () => void;
  openDetails: (
    date: string,
    mealType: number,
    rootItem: PlanMealRootItemType
  ) => void;
  closeDetails: () => void;
  openIntake: (date: string, mealType: number) => void;
  closeIntake: () => void;
  /** Refetch the week on screen, bypassing the cache. */
  reload: () => void;
  /** Read a day from its cached week (the drawers resolve their day this way). */
  getDay: (date: string) => NutritionDailyType | undefined;
};

export const NutritionPlanContext =
  createContext<NutritionPlanContextValue | null>(null);

type Props = {
  subjectId: string;
  role: number;
  canEdit?: boolean;
  fetchers?: NutritionPlanFetchers;
  /** Test seam: the date treated as "today". */
  today?: string;
  children: ReactNode;
};

const isNotFound = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 404;

/**
 * Mount with `key={subjectId}` so switching client drops every cache.
 */
export const NutritionPlanProvider = ({
  role,
  canEdit = false,
  fetchers = defaultFetchers,
  today,
  children,
}: Props) => {
  const [state, dispatch] = useReducer(
    nutritionPlanReducer,
    today ?? todayISO(),
    createInitialState
  );

  // One request per week at a time; components never fetch, so the rate
  // limit is respected no matter how many of them read the same week.
  const inFlightRef = useRef<Record<string, Promise<void>>>({});
  const fetchersRef = useRef(fetchers);
  fetchersRef.current = fetchers;
  const stateRef = useRef(state);
  stateRef.current = state;

  const ensureWeekly = useCallback(
    (monday: string, force = false): Promise<void> => {
      const status = stateRef.current.weeklyStatus[monday];
      if (!force && status && status !== "idle" && status !== "error") {
        return Promise.resolve();
      }
      const inFlight = inFlightRef.current[monday];
      if (inFlight) return inFlight;

      dispatch({ type: "WEEKLY_LOADING", monday });
      const run = (async () => {
        try {
          const plan = await fetchersRef.current.getWeekly(monday, role);
          dispatch({ type: "WEEKLY_LOADED", monday, plan });
        } catch (error) {
          if (isNotFound(error)) {
            dispatch({ type: "WEEKLY_LOADED", monday, plan: null });
          } else {
            console.error("[NutritionPlan] weekly plan failed", monday, error);
            dispatch({ type: "WEEKLY_FAILED", monday });
          }
        } finally {
          delete inFlightRef.current[monday];
        }
      })();
      inFlightRef.current[monday] = run;
      return run;
    },
    [role]
  );

  // Fetch the week on screen. Cached weeks return immediately.
  useEffect(() => {
    ensureWeekly(state.weekStartDate);
  }, [state.weekStartDate, ensureWeekly]);

  const reload = useCallback(() => {
    ensureWeekly(stateRef.current.weekStartDate, true);
  }, [ensureWeekly]);

  const activeWeek = state.weeklyCache[state.weekStartDate];
  const activeWeekStatus = state.weeklyStatus[state.weekStartDate] ?? "idle";

  const value = useMemo<NutritionPlanContextValue>(
    () => ({
      weekStartDate: state.weekStartDate,
      activeWeek,
      activeWeekStatus,
      details: state.details,
      intake: state.intake,
      canEdit,
      goToWeek: (monday) => dispatch({ type: "GO_TO_WEEK", monday }),
      goToPreviousWeek: () =>
        dispatch({
          type: "GO_TO_WEEK",
          monday: addDaysISO(stateRef.current.weekStartDate, -7),
        }),
      goToNextWeek: () =>
        dispatch({
          type: "GO_TO_WEEK",
          monday: addDaysISO(stateRef.current.weekStartDate, 7),
        }),
      goToThisWeek: () =>
        dispatch({ type: "GO_TO_WEEK", monday: thisWeekMonday() }),
      openDetails: (date, mealType, rootItem) =>
        dispatch({ type: "OPEN_DETAILS", date, mealType, rootItem }),
      closeDetails: () => dispatch({ type: "CLOSE_DETAILS" }),
      openIntake: (date, mealType) =>
        dispatch({ type: "OPEN_INTAKE", date, mealType }),
      closeIntake: () => dispatch({ type: "CLOSE_INTAKE" }),
      reload,
      getDay: (date) => selectDay(stateRef.current, date),
    }),
    [state, activeWeek, activeWeekStatus, canEdit, reload]
  );

  return (
    <NutritionPlanContext.Provider value={value}>
      {children}
    </NutritionPlanContext.Provider>
  );
};

export default NutritionPlanProvider;
